import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import Icon from '../components/Icon';
import { LogoMark } from '../components/Logo';
import { FieldLabel, FieldInput, FieldTextarea } from '../components/FormField';
import { partnershipBenefits, contactInfo } from '../mock';
import { trackPartnershipSubmit } from '../lib/analytics';

// Partnership inquiries (agency/agent) are routed to a dedicated mailbox,
// separate from the general contactInfo.email used elsewhere on the site.
const PARTNERSHIP_EMAIL = 'office@zeevogo.com';

const agencyBenefits = [
  { icon: 'dollar', title: 'Attractive Commissions', description: 'Earn up to 15% commission on every successful placement with transparent pricing' },
  { icon: 'headset', title: 'Dedicated Support', description: 'Get assigned a dedicated account manager for personalized assistance' },
  { icon: 'fileText', title: 'Marketing Materials', description: 'Access professionally designed marketing collateral and brand assets' },
  { icon: 'trendingUp', title: 'Growth Opportunities', description: 'Scale your business with our expanding portfolio of European opportunities' },
  { icon: 'users', title: 'CRM Access', description: 'Track and manage all your candidates through our advanced CRM system' },
  { icon: 'timer', title: 'Fast Processing', description: 'Benefit from our streamlined visa and documentation processes' },
];

const agentBenefits = [
  { icon: 'zap', title: 'Zero Investment', description: 'Start earning immediately without any upfront costs or fees' },
  { icon: 'target', title: 'Flexible Hours', description: 'Work at your own pace and schedule that fits your lifestyle' },
  { icon: 'trendingUp', title: 'Earn Per Placement', description: 'Attractive commission on every successful candidate placement' },
  { icon: 'bookOpen', title: 'Training Provided', description: 'Comprehensive training and certification to help you succeed' },
  { icon: 'award', title: 'Performance Incentives', description: 'Extra rewards and bonuses for top-performing agents' },
  { icon: 'checkCircle', title: 'Support & Tools', description: 'Access to marketing materials and dedicated support team' },
];

const howToEarn = [
  { step: '1', title: 'Register as Agent', description: 'Complete the registration form and get verified' },
  { step: '2', title: 'Get Training', description: 'Attend our comprehensive training program' },
  { step: '3', title: 'Find Candidates', description: 'Connect with people looking for European jobs' },
  { step: '4', title: 'Earn Commission', description: 'Get paid for every successful placement' },
];

const BenefitGrid = ({ items }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
    {items.map((item) => (
      <div
        key={item.title}
        className="rounded-[18px] border p-6 transition-all"
        style={{ borderColor: 'var(--zg-border)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#e08e0b';
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 18px 40px rgba(45,42,36,.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--zg-border)';
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div className="w-[54px] h-[54px] rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(224,142,11,.12)', color: '#c67e0a' }}>
          <Icon name={item.icon} size={26} />
        </div>
        <div className="text-xl font-bold mb-2" style={{ color: 'var(--zg-ink)' }}>
          {item.title}
        </div>
        <p style={{ color: 'var(--zg-muted)' }}>{item.description}</p>
      </div>
    ))}
  </div>
);

const SubmitButton = ({ submitting, children }) => (
  <button
    type="submit"
    disabled={submitting}
    className="flex items-center justify-center h-14 rounded-full text-base font-medium text-white transition-all disabled:opacity-70"
    style={{ background: '#2d2a24' }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.background = '#3a362e';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'none';
      e.currentTarget.style.background = '#2d2a24';
    }}
  >
    {children}
  </button>
);

const ContactLine = () => (
  <div className="mt-12 text-center space-y-4">
    <p style={{ color: 'var(--zg-muted)' }}>Have questions? Contact our partnership team</p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <a href={`tel:${contactInfo.phone}`} className="font-medium no-underline" style={{ color: 'var(--zg-ink)' }}>
        {contactInfo.phone}
      </a>
      <span className="hidden sm:inline" style={{ color: '#b4ae9f' }}>
        |
      </span>
      <a href={`mailto:${PARTNERSHIP_EMAIL}`} className="font-medium no-underline" style={{ color: 'var(--zg-ink)' }}>
        {PARTNERSHIP_EMAIL}
      </a>
    </div>
  </div>
);

const AgencyPanel = () => {
  const [formData, setFormData] = useState({
    agencyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    companySize: '',
    experience: '',
    location: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/partnership-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partnership_type: 'agency',
          full_name: formData.contactPerson,
          company_name: formData.agencyName,
          company_size: formData.companySize || null,
          email: formData.email,
          phone: formData.phone,
          location: formData.location || null,
          experience: formData.experience || null,
          message: formData.message || null,
        }),
      });

      if (!res.ok) {
        trackPartnershipSubmit('agency', 'error');
        toast.error(
          `We couldn't submit your application right now. Please try again, or reach us directly at ${contactInfo.phone} / ${PARTNERSHIP_EMAIL}.`
        );
        return;
      }

      trackPartnershipSubmit('agency', 'success');
      toast.success('Application submitted successfully! We will contact you within 24 hours.');
      setFormData({
        agencyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        companySize: '',
        experience: '',
        location: '',
        message: '',
      });
    } catch (err) {
      trackPartnershipSubmit('agency', 'error');
      toast.error(
        `We couldn't submit your application right now. Please try again, or reach us directly at ${contactInfo.phone} / ${PARTNERSHIP_EMAIL}.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <section className="px-6" style={{ padding: '64px 24px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-3xl">
          <div
            className="inline-flex items-center gap-2 text-white rounded-full px-4 py-2 mb-6 text-sm font-medium"
            style={{ background: '#2d2a24' }}
          >
            <Icon name="building" size={16} color="#fff" />
            <span>Agency Partnership Program</span>
          </div>
          <h2 className="font-bold mb-6 leading-tight" style={{ fontSize: 44, color: 'var(--zg-ink)' }}>
            Tie Up With Zeevogo as a Recruitment Partner
          </h2>
          <p className="text-xl leading-relaxed" style={{ color: 'var(--zg-muted)' }}>
            If you run a recruitment, staffing, or placement agency, tie up with Zeevogo to supply candidates for
            our verified job openings across India and Europe. You bring the candidates from your existing
            network — we handle employer verification, documentation, and visa processing — and pay you a
            transparent, volume-based commission on every candidate we successfully place.
          </p>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              Why Partner With Us?
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: 'var(--zg-muted)' }}>
              Built for agencies that want to add international placements to their business, without taking on
              the operational overhead of employer verification and visa processing themselves
            </p>
          </div>
          <BenefitGrid items={agencyBenefits} />
        </div>
      </section>

      <section className="relative overflow-hidden px-6" style={{ padding: '88px 24px', background: '#2d2a24', color: '#fff' }}>
        <LogoMark size={480} extraRing ink="#fff" fill="#fff" dotFill="#2d2a24" opacity={0.06} style={{ position: 'absolute', right: -100, bottom: -110, pointerEvents: 'none' }} />
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto relative">
          <div>
            <h2 className="text-4xl font-bold mb-6">What a Zeevogo Tie-Up Includes</h2>
            <p className="text-xl mb-8" style={{ color: '#b4ae9f' }}>
              Our agency partnership is built to slot into how your business already works — you supply and
              pre-screen candidates, we manage employer verification, documentation, and visa processing from
              there.
            </p>
            <ul className="list-none p-0 m-0 flex flex-col gap-4">
              {partnershipBenefits.agency.map((benefit) => (
                <li key={benefit} className="flex gap-3 text-lg">
                  ✓ {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <div className="rounded-[18px] p-6" style={{ background: '#2a2721', border: '1px solid #3a362e' }}>
              <div className="text-[22px] font-bold mb-4">Commission Structure</div>
              <div className="flex flex-col gap-3">
                {[
                  ['1-10 Placements', '10%'],
                  ['11-50 Placements', '12%'],
                  ['51+ Placements', '15%'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center rounded-[18px] px-4 py-4" style={{ background: '#3a362e' }}>
                    <span style={{ color: 'var(--zg-border2)' }}>{label}</span>
                    <span className="text-[22px] font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[18px] p-6" style={{ background: '#2a2721', border: '1px solid #3a362e' }}>
              <p className="text-sm m-0" style={{ color: '#b4ae9f' }}>
                * Commission rates are calculated on a quarterly basis and include all successfully placed
                candidates with completed visa processing.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              Register Your Agency
            </h2>
            <p className="text-xl" style={{ color: 'var(--zg-muted)' }}>
              Tell us about your agency, and our partnerships team will reach out within 24 hours to set up your
              tie-up
            </p>
          </div>

          <div
            className="rounded-[24px] border px-[34px] py-10"
            style={{ borderColor: 'var(--zg-border)', background: 'var(--zg-card)', boxShadow: '0 20px 50px rgba(45,42,36,.07)' }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel htmlFor="agencyName">Agency Name *</FieldLabel>
                  <FieldInput id="agencyName" name="agencyName" value={formData.agencyName} onChange={handleChange} required placeholder="Your agency name" />
                </div>
                <div>
                  <FieldLabel htmlFor="contactPerson">Contact Person *</FieldLabel>
                  <FieldInput id="contactPerson" name="contactPerson" value={formData.contactPerson} onChange={handleChange} required placeholder="Full name" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel htmlFor="email">Email Address *</FieldLabel>
                  <FieldInput id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="agency@example.com" />
                </div>
                <div>
                  <FieldLabel htmlFor="phone">Phone Number *</FieldLabel>
                  <FieldInput id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="+91 XXXXXXXXXX" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel htmlFor="companySize">Company Size</FieldLabel>
                  <FieldInput id="companySize" name="companySize" value={formData.companySize} onChange={handleChange} placeholder="e.g., 10-50 employees" />
                </div>
                <div>
                  <FieldLabel htmlFor="experience">Years of Experience</FieldLabel>
                  <FieldInput id="experience" name="experience" value={formData.experience} onChange={handleChange} placeholder="e.g., 5 years" />
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="location">Location</FieldLabel>
                <FieldInput id="location" name="location" value={formData.location} onChange={handleChange} placeholder="City, State, Country" />
              </div>

              <div>
                <FieldLabel htmlFor="message">Message / Additional Information</FieldLabel>
                <FieldTextarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your agency and why you'd like to partner with Zeevogo..."
                />
              </div>

              <SubmitButton submitting={submitting}>{submitting ? 'Submitting...' : 'Submit Application'}</SubmitButton>
            </form>
          </div>

          <ContactLine />
        </div>
      </section>
    </div>
  );
};

const AgentPanel = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    availability: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/partnership-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partnership_type: 'agent',
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          experience: formData.experience || null,
          availability: formData.availability || null,
          message: formData.message || null,
        }),
      });

      if (!res.ok) {
        trackPartnershipSubmit('agent', 'error');
        toast.error(
          `We couldn't submit your application right now. Please try again, or reach us directly at ${contactInfo.phone} / ${PARTNERSHIP_EMAIL}.`
        );
        return;
      }

      trackPartnershipSubmit('agent', 'success');
      toast.success('Application submitted successfully! We will contact you within 24 hours.');
      setFormData({ fullName: '', email: '', phone: '', location: '', experience: '', availability: '', message: '' });
    } catch (err) {
      trackPartnershipSubmit('agent', 'error');
      toast.error(
        `We couldn't submit your application right now. Please try again, or reach us directly at ${contactInfo.phone} / ${PARTNERSHIP_EMAIL}.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <section className="px-6" style={{ padding: '64px 24px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-3xl">
          <div
            className="inline-flex items-center gap-2 text-white rounded-full px-4 py-2 mb-6 text-sm font-medium"
            style={{ background: '#2d2a24' }}
          >
            <Icon name="userCheck" size={16} color="#fff" />
            <span>Agent Partnership Program</span>
          </div>
          <h2 className="font-bold mb-6 leading-tight" style={{ fontSize: 44, color: 'var(--zg-ink)' }}>
            Refer Candidates, Earn Commission
          </h2>
          <p className="text-xl leading-relaxed mb-8" style={{ color: 'var(--zg-muted)' }}>
            Do you know people looking for work abroad — in your community, your family's network, or your
            circle? As a Zeevogo agent, you don't need a company, an office, or any investment. You simply refer
            candidates to us; we handle screening, documentation, and visa processing — and you earn a fixed
            commission for every candidate who's successfully placed.
          </p>
          <div className="flex flex-wrap gap-8 items-center">
            <div>
              <div className="text-3xl font-bold" style={{ color: '#e08e0b' }}>₹25,000+</div>
              <div className="text-sm" style={{ color: 'var(--zg-muted)' }}>Avg. Monthly Earnings</div>
            </div>
            <div className="h-12 w-px" style={{ background: 'var(--zg-border2)' }} />
            <div>
              <div className="text-3xl font-bold" style={{ color: '#e08e0b' }}>500+</div>
              <div className="text-sm" style={{ color: 'var(--zg-muted)' }}>Active Agents</div>
            </div>
            <div className="h-12 w-px" style={{ background: 'var(--zg-border2)' }} />
            <div>
              <div className="text-3xl font-bold" style={{ color: '#e08e0b' }}>24/7</div>
              <div className="text-sm" style={{ color: 'var(--zg-muted)' }}>Support Available</div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              Why Become an Agent?
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: 'var(--zg-muted)' }}>
              Built for individuals who want to earn by connecting people they know to real opportunities — no
              business registration needed
            </p>
          </div>
          <BenefitGrid items={agentBenefits} />
        </div>
      </section>

      <section className="relative overflow-hidden px-6" style={{ padding: '88px 24px', background: '#2d2a24', color: '#fff' }}>
        <LogoMark size={480} extraRing ink="#fff" fill="#fff" dotFill="#2d2a24" opacity={0.06} style={{ position: 'absolute', right: -100, bottom: -110, pointerEvents: 'none' }} />
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto relative">
          <div>
            <h2 className="text-4xl font-bold mb-6">What You'll Get as an Agent</h2>
            <p className="text-xl mb-8" style={{ color: '#b4ae9f' }}>
              We provide all the tools, training, and support you need to succeed as a recruitment agent.
            </p>
            <ul className="list-none p-0 m-0 flex flex-col gap-4">
              {partnershipBenefits.agent.map((benefit) => (
                <li key={benefit} className="flex gap-3 text-lg">
                  ✓ {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <div className="rounded-[18px] p-6" style={{ background: '#2a2721', border: '1px solid #3a362e' }}>
              <div className="text-[22px] font-bold mb-1">Earning Potential</div>
              <div className="text-sm mb-4" style={{ color: '#b4ae9f' }}>Commission per successful placement</div>
              <div className="flex flex-col gap-3">
                {[
                  ['Delivery Agent', '₹15,000'],
                  ['Factory Worker', '₹12,000'],
                  ['Bonus (10+ placements/month)', '+₹10,000'],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center rounded-[18px] px-4 py-4" style={{ background: '#3a362e' }}>
                    <span style={{ color: 'var(--zg-border2)' }}>{label}</span>
                    <span className="text-[22px] font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[18px] p-6" style={{ background: '#2a2721', border: '1px solid #3a362e' }}>
              <p className="text-sm m-0" style={{ color: '#b4ae9f' }}>
                * Commission is paid after successful visa approval and candidate departure. Additional
                performance bonuses available.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              How to Start Earning
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: 'var(--zg-muted)' }}>
              Getting started is simple and straightforward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howToEarn.map((item) => (
              <div key={item.step} className="text-center">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white font-bold text-2xl mb-4"
                  style={{ background: '#e08e0b' }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--zg-ink)' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--zg-muted)' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              Register as an Agent
            </h2>
            <p className="text-xl" style={{ color: 'var(--zg-muted)' }}>
              Tell us a bit about yourself, and our team will verify and onboard you within 24 hours. No
              investment required.
            </p>
          </div>

          <div
            className="rounded-[24px] border px-[34px] py-10"
            style={{ borderColor: 'var(--zg-border)', background: 'var(--zg-card)', boxShadow: '0 20px 50px rgba(45,42,36,.07)' }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div>
                <FieldLabel htmlFor="fullName">Full Name *</FieldLabel>
                <FieldInput id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="Enter your full name" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel htmlFor="email">Email Address *</FieldLabel>
                  <FieldInput id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" />
                </div>
                <div>
                  <FieldLabel htmlFor="phone">Phone Number *</FieldLabel>
                  <FieldInput id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="+91 XXXXXXXXXX" />
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="location">Location *</FieldLabel>
                <FieldInput id="location" name="location" value={formData.location} onChange={handleChange} required placeholder="City, State" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel htmlFor="experience">Prior Experience (if any)</FieldLabel>
                  <FieldInput id="experience" name="experience" value={formData.experience} onChange={handleChange} placeholder="e.g., Recruitment, Sales" />
                </div>
                <div>
                  <FieldLabel htmlFor="availability">Availability</FieldLabel>
                  <FieldInput id="availability" name="availability" value={formData.availability} onChange={handleChange} placeholder="e.g., Full-time, Part-time" />
                </div>
              </div>

              <div>
                <FieldLabel htmlFor="message">Why do you want to become an agent?</FieldLabel>
                <FieldTextarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your motivation and what makes you a good fit..."
                />
              </div>

              <SubmitButton submitting={submitting}>{submitting ? 'Submitting...' : 'Submit Application'}</SubmitButton>
            </form>
          </div>

          <ContactLine />
        </div>
      </section>
    </div>
  );
};

const PartnershipPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const activeTab = type === 'agent' ? 'agent' : 'agency';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--zg-bg)' }}>
      <div className="px-6" style={{ padding: '96px 24px 0', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-7xl">
          <Link to="/" className="inline-flex items-center gap-2 no-underline" style={{ color: 'var(--zg-muted)' }}>
            <Icon name="arrowLeft" size={16} />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <section className="px-6 text-center" style={{ padding: '32px 24px 40px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-3xl">
          <div className="text-[13px] font-bold uppercase mb-3" style={{ letterSpacing: '.14em', color: '#b5720a' }}>
            Three Ways To Work With Zeevogo
          </div>
          <h1 className="font-bold mb-6 leading-tight" style={{ fontSize: 48, color: 'var(--zg-ink)' }}>
            Partner With Zeevogo
          </h1>
          <p className="text-xl leading-relaxed" style={{ color: 'var(--zg-muted)' }}>
            We place candidates in verified jobs across India and Europe through three channels. Candidates can{' '}
            <Link to="/jobs" style={{ color: 'var(--zg-ink)', fontWeight: 600 }}>
              apply directly
            </Link>{' '}
            to our open positions. Recruitment agencies can tie up with us to supply candidates at scale, earning a
            tiered commission on every placement. Individual agents can refer candidates from their own network —
            no company or office required — and earn a commission for each successful placement. Pick the track
            below that fits you.
          </p>
        </div>
      </section>

      <div className="px-6 flex justify-center" style={{ padding: '0 24px 0', background: 'var(--zg-surface)' }}>
        <div
          className="inline-flex flex-wrap justify-center rounded-full border p-1.5 gap-1"
          style={{ background: 'var(--zg-card)', borderColor: 'var(--zg-border)', boxShadow: '0 8px 24px rgba(45,42,36,.06)' }}
        >
          <button
            type="button"
            onClick={() => navigate('/partnership/agency')}
            className="flex items-center gap-2 rounded-full text-sm font-semibold px-[22px] py-2.5 transition-all"
            style={{
              background: activeTab === 'agency' ? '#2d2a24' : 'transparent',
              color: activeTab === 'agency' ? '#fff' : 'var(--zg-muted)',
            }}
          >
            <Icon name="building" size={16} />
            Agency Partnership
          </button>
          <button
            type="button"
            onClick={() => navigate('/partnership/agent')}
            className="flex items-center gap-2 rounded-full text-sm font-semibold px-[22px] py-2.5 transition-all"
            style={{
              background: activeTab === 'agent' ? '#2d2a24' : 'transparent',
              color: activeTab === 'agent' ? '#fff' : 'var(--zg-muted)',
            }}
          >
            <Icon name="userCheck" size={16} />
            Agent Partnership
          </button>
        </div>
      </div>

      {activeTab === 'agency' ? <AgencyPanel /> : <AgentPanel />}
    </div>
  );
};

export default PartnershipPage;
